import { useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from 'react'
import { useAuth } from '../contexts/AuthContext'
import {
  FiLoader, FiChevronLeft, FiChevronRight, FiDownload, FiTrendingUp, FiPieChart,
  FiCreditCard, FiChevronDown, FiChevronUp, FiBarChart2,
} from 'react-icons/fi'
import { CategoryDonut, MonthlyFlowChart, FlowBarsChart, mesCurto, type MesFluxo } from './charts'
import { MONEY } from '../lib/chartTheme'
import { BankLogo } from './BankLogo'
import { PanelShell, PlnBarra, PlnBarraRot, PlnBarraSep, PlnKpis, PlnKpi, PlnBloco, stickyTh, stickyFoot } from './PanelShell'
import { ItensCategoria } from './ItensCategoria'

// Fluxo de caixa (PRD §5.9): regime de CAIXA (realizado). Aba 1 = fluxo por dia/
// mês com gráfico + tabela e saldo acumulado; aba 2 = análise por categoria.

interface FluxoRow { key: string; label: string; recebimentos: number; pagamentos: number; transf_in: number; transf_out: number; saldo: number }
interface FluxoData { granularity: 'dia' | 'mes'; opening: number; saldoFinal: number; account: number | null; totals: { recebimentos: number; pagamentos: number; transf_in: number; transf_out: number }; rows: FluxoRow[] }
interface CatRow { category_id: number; category_name: string; category_kind: string; valor: number; av: number; em_aberto: number; vencido: number; baixado: number }
interface CatData { month: string; total: number; categorias: CatRow[] }
interface Account { id: number; name: string; is_pj_main: number; bank_code?: string | null; type?: string | null }

const brl = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const thisMonth = () => new Date().toISOString().slice(0, 7)
const thisYear = () => String(new Date().getUTCFullYear())
const monthLabel = (m: string) => {
  const [y, mo] = m.split('-').map(Number)
  const s = new Date(Date.UTC(y, mo - 1, 1)).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric', timeZone: 'UTC' })
  return s.charAt(0).toUpperCase() + s.slice(1) // "Maio de 2026" (não capitalizar o "de")
}
const addMonth = (m: string, n: number) => {
  const [y, mo] = m.split('-').map(Number)
  const d = new Date(Date.UTC(y, mo - 1 + n, 1))
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}

export function FluxoCaixa() {
  const { authenticatedFetch } = useAuth()
  const [tab, setTab] = useState<'fluxo' | 'categorias'>('fluxo')
  const [accounts, setAccounts] = useState<Account[]>([])

  useEffect(() => {
    authenticatedFetch('/api/bank-accounts').then(r => r.json()).then(d => { if (d.success) setAccounts(d.items || []) }).catch(() => {})
  }, [authenticatedFetch])

  return (
    <PanelShell
      titulo="Fluxo de caixa"
      sub={tab === 'fluxo' ? 'Entradas, saídas e saldo: o mês de relance e o detalhe dia a dia' : 'Para onde foi o dinheiro, por categoria'}
      icone={<FiBarChart2 />}
      acoes={
        <div className="neu-seg inline-flex rounded-lg border border-border p-0.5 bg-card">
          <button onClick={() => setTab('fluxo')} className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md transition-colors ${tab === 'fluxo' ? 'neu-relief bg-primary/10 text-primary font-medium' : 'neu-seg-btn text-muted-foreground hover:text-foreground'}`}>
            <FiTrendingUp className="w-4 h-4" /> Fluxo de caixa
          </button>
          <button onClick={() => setTab('categorias')} className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md transition-colors ${tab === 'categorias' ? 'neu-relief bg-primary/10 text-primary font-medium' : 'neu-seg-btn text-muted-foreground hover:text-foreground'}`}>
            <FiPieChart className="w-4 h-4" /> Análise por categorias
          </button>
        </div>
      }>
      {tab === 'fluxo' ? <FluxoTab accounts={accounts} /> : <CategoriasTab />}
    </PanelShell>
  )
}

// ── Aba 1: Fluxo de caixa ──
function FluxoTab({ accounts }: { accounts: Account[] }) {
  const { authenticatedFetch } = useAuth()
  // Visão geral mês a mês (21/10/2026) é a porta de entrada; Diário/Ano/Período
  // continuam para quem quer o detalhe de um mês ou de um ano.
  const [visao, setVisao] = useState(true)
  const [granularity, setGranularity] = useState<'dia' | 'mes'>('dia')
  const [period, setPeriod] = useState(thisMonth())
  // Período personalizado DE/ATÉ — buckets diários no intervalo (até 366 dias)
  const [customRange, setCustomRange] = useState(false)
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  // Filtro multi-conta (uma, várias ou todas) — mesmo padrão do Extrato §5.8
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [pickerOpen, setPickerOpen] = useState(false)
  const pickerRef = useRef<HTMLDivElement>(null)
  const [data, setData] = useState<FluxoData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!pickerOpen) return
    const onDown = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setPickerOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [pickerOpen])

  const toggleAccount = (id: number) => setSelected(prev => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })
  const pickerLabel = selected.size === 0 ? 'Todas as contas'
    : selected.size === 1 ? (accounts.find(a => a.id === [...selected][0])?.name || '1 conta')
    : `${selected.size} contas`

  const switchGranularity = (g: 'dia' | 'mes') => { setVisao(false); setCustomRange(false); setGranularity(g); setPeriod(g === 'mes' ? thisYear() : thisMonth()) }
  const navPeriod = (dir: number) => setPeriod(p => granularity === 'mes' ? String(Number(p) + dir) : addMonth(p, dir))

  const load = useCallback(async () => {
    if (visao) return // a Visão geral busca a própria rota (/visao)
    if (customRange && (!fromDate || !toDate)) return // aguarda as duas datas
    setLoading(true); setError(null)
    try {
      const params = new URLSearchParams({ granularity, period })
      if (customRange && fromDate && toDate) { params.set('from', fromDate); params.set('to', toDate) }
      if (selected.size) params.set('accounts', [...selected].join(','))
      const r = await authenticatedFetch(`/api/fluxo-caixa?${params}`)
      const d = await r.json()
      if (d.success) setData(d); else setError(d.error || 'Falha ao carregar')
    } catch { setError('Erro de conexão') }
    finally { setLoading(false) }
  }, [authenticatedFetch, granularity, period, selected, customRange, fromDate, toDate, visao])
  useEffect(() => { load() }, [load])

  const rows = data?.rows || []
  const showTransfer = selected.size > 0

  const exportCsv = () => {
    const head = ['Data', 'Recebimentos', 'Pagamentos', 'Transf. entrada', 'Transf. saída', 'Saldo Final']
    const lines = rows.map(r => [r.label, r.recebimentos, r.pagamentos, r.transf_in, r.transf_out, r.saldo].map(v => typeof v === 'number' ? v.toFixed(2).replace('.', ',') : v).join(';'))
    const csv = '﻿' + [head.join(';'), ...lines].join('\n')
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
    const a = document.createElement('a'); a.href = url; a.download = `fluxo-caixa-${customRange && fromDate && toDate ? `${fromDate}_${toDate}` : period}.csv`; a.click(); URL.revokeObjectURL(url)
  }


  const diasComMovimento = rows.filter(r => r.recebimentos || r.pagamentos || r.transf_in || r.transf_out).length
  const resultado = data ? data.totals.recebimentos - data.totals.pagamentos : 0
  const transfLiq = data ? data.totals.transf_in - data.totals.transf_out : 0
  // aplicação/resgate também entram em transf_* sem filtro de conta (ver backend)
  const comTransf = showTransfer || !!(data && (data.totals.transf_in || data.totals.transf_out))
  const porMes = granularity === 'mes' && !customRange
  const unidade = porMes ? 'meses' : 'dias'
  // recorte em datas — base das categorias e dos títulos
  const recorte = useMemo(() => {
    if (customRange) return fromDate && toDate ? { from: fromDate, to: toDate } : null
    if (granularity === 'mes') return { from: `${period}-01-01`, to: `${period}-12-31` }
    const [y, m] = period.split('-').map(Number)
    return { from: `${period}-01`, to: `${period}-${String(new Date(Date.UTC(y, m, 0)).getUTCDate()).padStart(2, '0')}` }
  }, [customRange, fromDate, toDate, granularity, period])
  const ptData = (d: string) => d.split('-').reverse().join('/')
  const tituloRecorte = customRange ? (recorte ? `De ${ptData(recorte.from)} a ${ptData(recorte.to)}` : 'Período') : porMes ? `Ano de ${period}` : monthLabel(period)
  const rotuloLinha = (r: FluxoRow) => porMes ? mesCurto(r.key) : customRange ? r.label : `${r.label}/${period.slice(5, 7)}`
  const pontos = rows.map(r => ({ key: r.key, label: porMes ? mesCurto(r.key) : r.label, titulo: rotuloLinha(r), entradas: r.recebimentos, saidas: r.pagamentos, saldo: r.saldo }))

  const [cats, setCats] = useState<CatMes[]>([])
  const [catLoading, setCatLoading] = useState(false)
  useEffect(() => {
    if (visao || !recorte) return
    let vivo = true
    setCatLoading(true)
    const params = new URLSearchParams({ from: recorte.from, to: recorte.to })
    if (selected.size) params.set('accounts', [...selected].join(','))
    authenticatedFetch(`/api/fluxo-caixa/categorias-caixa?${params}`).then(r => r.json())
      .then(j => { if (vivo && j.success) setCats(j.detalhe) })
      .catch(() => {})
      .finally(() => { if (vivo) setCatLoading(false) })
    return () => { vivo = false }
  }, [authenticatedFetch, visao, recorte, selected])

  return (
    <div className="flex-1 min-h-0 flex flex-col pln-corpo-tela">
      {/* controles — dentro da faixa de papel (PLANOS: nada solto no chão) */}
      <PlnBarra>
        <PlnBarraRot>Ver por</PlnBarraRot>
        <div className="neu-seg inline-flex rounded-lg border border-border p-0.5 bg-card">
          <button onClick={() => { setVisao(true); setCustomRange(false) }} title="Os últimos 12 meses e o que está a vencer, com o detalhe por categoria"
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${visao ? 'neu-relief bg-primary/10 text-primary font-medium' : 'neu-seg-btn text-muted-foreground hover:text-foreground'}`}>Visão geral</button>
          {([['dia', 'Diário'], ['mes', 'Ano']] as ['dia' | 'mes', string][]).map(([v, lbl]) => (
            <button key={v} onClick={() => switchGranularity(v)} className={`px-3 py-1.5 text-sm rounded-md transition-colors ${!visao && !customRange && granularity === v ? 'neu-relief bg-primary/10 text-primary font-medium' : 'neu-seg-btn text-muted-foreground hover:text-foreground'}`}>{lbl}</button>
          ))}
          <button onClick={() => { setVisao(false); setCustomRange(true) }} title="Intervalo de datas De/Até (dia a dia, até 366 dias)"
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${!visao && customRange ? 'neu-relief bg-primary/10 text-primary font-medium' : 'neu-seg-btn text-muted-foreground hover:text-foreground'}`}>Período</button>
        </div>
        {!visao && <PlnBarraSep />}
        {visao ? null : customRange ? (
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <span>De</span>
            <input type="date" value={fromDate} max={toDate || undefined} onChange={e => setFromDate(e.target.value)}
              title="Início do período" className="neu-convex rounded-lg border border-border bg-background px-2 py-2 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" />
            <span>até</span>
            <input type="date" value={toDate} min={fromDate || undefined} onChange={e => setToDate(e.target.value)}
              title="Fim do período" className="neu-convex rounded-lg border border-border bg-background px-2 py-2 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" />
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <button onClick={() => navPeriod(-1)} title="Anterior" className="neu-convex w-8 h-8 flex items-center justify-center rounded-md border border-border hover:bg-secondary"><FiChevronLeft className="w-4 h-4" /></button>
            <span className="text-sm font-medium text-foreground min-w-[140px] text-center">{granularity === 'mes' ? period : monthLabel(period)}</span>
            <button onClick={() => navPeriod(1)} title="Próximo" className="neu-convex w-8 h-8 flex items-center justify-center rounded-md border border-border hover:bg-secondary"><FiChevronRight className="w-4 h-4" /></button>
          </div>
        )}
        <PlnBarraSep />
        {/* Seletor de contas (multi) — mesmo padrão do Extrato de movimentações */}
        <div className="relative" ref={pickerRef}>
          <button onClick={() => setPickerOpen(o => !o)} title="Filtrar por conta (uma ou várias)"
            className={`inline-flex items-center gap-2 h-10 rounded-lg border px-2.5 text-sm transition-colors ${selected.size ? 'border-primary/40 bg-primary/5 text-foreground' : 'border-border bg-card text-foreground hover:bg-secondary'}`}>
            {selected.size === 1 ? (
              (() => { const a = accounts.find(x => x.id === [...selected][0]); return <BankLogo code={a?.bank_code} name={a?.name} type={a?.type} size={26} /> })()
            ) : (
              <FiCreditCard className={`w-4 h-4 ${selected.size ? 'text-primary' : 'text-muted-foreground'}`} />
            )}
            <span className="max-w-[220px] truncate font-medium">{pickerLabel}</span>
            <FiChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${pickerOpen ? 'rotate-180' : ''}`} />
          </button>
          {pickerOpen && (
            <div className="neu-pop absolute left-0 top-full mt-1 z-30 w-72 glass-panel rounded-xl border border-border shadow-lg p-2">
              <button onClick={() => setSelected(new Set())} title="Limpar filtro e voltar ao consolidado"
                className={`w-full text-left px-2.5 py-1.5 rounded-lg text-sm transition-colors ${selected.size === 0 ? 'bg-primary/10 text-primary font-medium' : 'text-foreground hover:bg-secondary'}`}>
                Todas as contas
              </button>
              <div className="my-1.5 border-t border-border/60" />
              <div className="max-h-64 overflow-y-auto scrollbar-thin">
                {accounts.map(a => (
                  <label key={a.id} title={`Incluir/remover ${a.name} do fluxo`}
                    className="neu-item flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-sm cursor-pointer hover:bg-secondary">
                    <input type="checkbox" checked={selected.has(a.id)} onChange={() => toggleAccount(a.id)}
                      className="neu-check w-4 h-4 accent-[hsl(var(--primary))] shrink-0" />
                    <BankLogo code={a.bank_code} name={a.name} type={a.type} size={22} />
                    <span className="truncate text-foreground">{a.name}{a.is_pj_main ? ' (PJ principal)' : ''}</span>
                  </label>
                ))}
                {accounts.length === 0 && (
                  <div className="px-2.5 py-2 text-xs text-muted-foreground">Nenhuma conta cadastrada.</div>
                )}
              </div>
            </div>
          )}
        </div>
        {/* Exportar mora no bloco da tabela (Diário/Ano/Período) e no da Visão geral */}
      </PlnBarra>

      {visao ? <VisaoMensal selected={selected} /> : (<>
      {error && <div className="shrink-0 text-sm text-red-500">{error}</div>}

      {/* Diário / Ano / Período no MESMO desenho da Visão geral (21/10/2026, o
          dono cobrou que essas visões ficaram para trás): cinco cartões, barras
          de entradas × saídas com o saldo, categorias do período ao lado e a
          tabela embaixo. */}
      <PlnKpis>
        <PlnKpi rotulo="Saldo de abertura" valor={data ? brl(data.opening) : '—'} pe={`no início ${granularity === 'mes' && !customRange ? 'do ano' : 'do período'}`} />
        <PlnKpi rotulo="Entradas" cor={MONEY.in} tom="ok" valor={data ? brl(data.totals.recebimentos) : '—'} pe={`${diasComMovimento} ${unidade} com movimento`} />
        <PlnKpi rotulo="Saídas" cor={MONEY.out} tom="ruim" valor={data ? brl(data.totals.pagamentos) : '—'} pe="pagamentos realizados" />
        <PlnKpi rotulo="Resultado" tom={resultado >= 0 ? 'ok' : 'ruim'} valor={data ? brl(resultado) : '—'} pe="entradas menos saídas" />
        <PlnKpi rotulo="Saldo final" cor={MONEY.balance} tom={data && data.saldoFinal < 0 ? 'ruim' : undefined} valor={data ? brl(data.saldoFinal) : '—'}
          pe={transfLiq ? `inclui ${brl(transfLiq)} de aplicações/transf.` : 'fechamento do período'} />
      </PlnKpis>

      <div className="pln-visao flex-1 min-h-0">
        <PlnBloco titulo="Entradas × saídas" nota={`${tituloRecorte} · linha = saldo no fim de cada ${granularity === 'mes' && !customRange ? 'mês' : 'dia'}`}
          extra={<div className="pln-legenda"><span><b style={{ background: MONEY.in }} />Entradas</span><span><b style={{ background: MONEY.out }} />Saídas</span><span><b style={{ background: MONEY.balance }} />Saldo</span></div>}
          className="pln-visao-graf">
          <div className="flex-1 min-h-0 px-3 pt-3 pb-2">
            {loading && !rows.length ? <div className="h-full flex items-center justify-center"><FiLoader className="w-5 h-5 animate-spin text-muted-foreground" /></div>
              : rows.length ? <FlowBarsChart pontos={pontos} barSize={granularity === 'mes' && !customRange ? 38 : 18} />
              : <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Sem movimentação de caixa no período.</div>}
          </div>
        </PlnBloco>

        <PlnBloco titulo={tituloRecorte} nota="por categoria · o que foi pago e recebido" className="pln-visao-cat">
          <div className={`flex-1 min-h-0 overflow-y-auto scrollbar-thin px-[18px] pb-4 transition-opacity ${catLoading ? 'opacity-60' : ''}`}>
            <CategoriasDoPeriodo itens={cats} />
          </div>
        </PlnBloco>

        <PlnBloco titulo={granularity === 'mes' && !customRange ? 'Mês a mês' : 'Dia a dia'} nota={`${diasComMovimento} ${unidade} com movimento`}
          extra={<button onClick={exportCsv} disabled={!rows.length} className="neu-convex inline-flex items-center gap-2 rounded-lg border border-border text-sm font-medium px-3 py-1.5 hover:bg-secondary disabled:opacity-40"><FiDownload className="w-4 h-4" /> Exportar</button>}
          className="pln-visao-tab">
      <div className="glass-panel neu-table flex-1 min-h-0 overflow-auto scrollbar-thin">
        <table className="w-full text-sm min-w-[680px]">
          <thead>
            <tr className="text-left text-xs text-muted-foreground">
              <th className={`px-4 py-2.5 font-medium w-24 ${stickyTh}`}>{granularity === 'mes' && !customRange ? 'Mês' : 'Data'}</th>
              <th className={`px-4 py-2.5 font-medium text-right ${stickyTh}`}>Entradas</th>
              <th className={`px-4 py-2.5 font-medium text-right ${stickyTh}`}>Saídas</th>
              {comTransf && <th className={`px-4 py-2.5 font-medium text-right ${stickyTh}`} title="Aplicação/resgate e transferências entre contas: mexem no saldo, mas não são operação">Aplic./transf.</th>}
              <th className={`px-4 py-2.5 font-medium text-right ${stickyTh}`}>Resultado</th>
              <th className={`px-4 py-2.5 font-medium text-right ${stickyTh}`}>Saldo final</th>
            </tr>
          </thead>
          <tbody>
            {loading && !rows.length ? (
              <tr><td colSpan={comTransf ? 6 : 5} className="px-4 py-10 text-center text-muted-foreground"><FiLoader className="w-5 h-5 animate-spin inline" /></td></tr>
            ) : (
              <>
                <tr className="neu-band-1 border-b border-border/60 bg-secondary/20 text-xs text-muted-foreground">
                  <td className="px-4 py-2">Abertura</td>
                  <td colSpan={comTransf ? 4 : 3}></td>
                  <td className="px-4 py-2 text-right font-medium">{data ? brl(data.opening) : '—'}</td>
                </tr>
                {rows.filter(r => r.recebimentos || r.pagamentos || r.transf_in || r.transf_out).map(r => {
                  const res = r.recebimentos - r.pagamentos, tr = r.transf_in - r.transf_out
                  return (
                  <tr key={r.key} className="neu-tr border-b border-border/60 hover:bg-secondary/40">
                    <td className="px-4 py-2 text-foreground whitespace-nowrap capitalize">{rotuloLinha(r)}</td>
                    <td className="px-4 py-2 text-right text-primary tabular-nums">{r.recebimentos ? brl(r.recebimentos) : '—'}</td>
                    <td className="px-4 py-2 text-right text-red-600 dark:text-red-400 tabular-nums">{r.pagamentos ? brl(r.pagamentos) : '—'}</td>
                    {comTransf && <td className="px-4 py-2 text-right text-muted-foreground tabular-nums">{tr ? brl(tr) : '—'}</td>}
                    <td className={`px-4 py-2 text-right tabular-nums ${res >= 0 ? 'text-primary' : 'text-red-600 dark:text-red-400'}`}>{brl(res)}</td>
                    <td className="px-4 py-2 text-right font-semibold text-foreground tabular-nums">{brl(r.saldo)}</td>
                  </tr>
                  )
                })}
              </>
            )}
          </tbody>
          {data && rows.length > 0 && (
            <tfoot>
              <tr className="font-medium">
                <td className={`px-4 py-2.5 text-muted-foreground ${stickyFoot}`}>Totais</td>
                <td className={`px-4 py-2.5 text-right text-primary ${stickyFoot}`}>{brl(data.totals.recebimentos)}</td>
                <td className={`px-4 py-2.5 text-right text-red-600 dark:text-red-400 ${stickyFoot}`}>{brl(data.totals.pagamentos)}</td>
                {comTransf && <td className={`px-4 py-2.5 text-right text-muted-foreground ${stickyFoot}`}>{brl(transfLiq)}</td>}
                <td className={`px-4 py-2.5 text-right ${stickyFoot} ${resultado >= 0 ? 'text-primary' : 'text-red-600 dark:text-red-400'}`}>{brl(resultado)}</td>
                <td className={`px-4 py-2.5 text-right ${stickyFoot} ${data.saldoFinal < 0 ? 'text-red-600 dark:text-red-400' : 'text-foreground'}`}>{brl(data.saldoFinal)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
        </PlnBloco>
      </div>
      </>)}
    </div>
  )
}

// ── Visão geral mês a mês (21/10/2026) ──
// O diretor comparou com o fluxo do /seals ("muito melhor"): lá se lê o MÊS de
// relance — entradas × saídas, o saldo andando e para onde foi o dinheiro. Aqui
// é o mesmo desenho sobre a rota /visao, que usa a MESMA conta do realizado do
// dia a dia (calcRealizado). O que está a vencer aparece em tom claro; meses
// futuros quase vazios são verdade, não defeito (no fin a despesa entra já paga).
interface CatMes { kind: string; id: number; name: string; real_v: number; prev_v: number }
interface VisaoData { mes: string; hoje: string; opening: number; serie: MesFluxo[]; detalhe: CatMes[] }

function VisaoMensal({ selected }: { selected: Set<number> }) {
  const { authenticatedFetch } = useAuth()
  const [mes, setMes] = useState<string | null>(null)
  const [d, setD] = useState<VisaoData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let vivo = true
    setLoading(true); setError(null)
    const params = new URLSearchParams()
    if (mes) params.set('mes', mes)
    if (selected.size) params.set('accounts', [...selected].join(','))
    authenticatedFetch(`/api/fluxo-caixa/visao?${params}`).then(r => r.json())
      .then(j => { if (!vivo) return; if (j.success) setD(j); else setError(j.error || 'Falha ao carregar') })
      .catch(() => { if (vivo) setError('Erro de conexão') })
      .finally(() => { if (vivo) setLoading(false) })
    return () => { vivo = false }
  }, [authenticatedFetch, mes, selected])

  // Meses antes do primeiro lançamento (a base começa em jan/2026) não dizem nada.
  const serie = useMemo(() => {
    const all = d?.serie || []
    const i = all.findIndex(m => m.entradas || m.saidas || m.entradas_prev || m.saidas_prev || m.transf_in || m.transf_out)
    return i > 0 ? all.slice(i) : all
  }, [d])

  if (!d) {
    return error ? <div className="shrink-0 text-sm text-red-500">{error}</div>
      : <div className="flex-1 flex items-center justify-center text-muted-foreground"><FiLoader className="w-5 h-5 animate-spin" /></div>
  }

  const cur = d.serie.find(m => m.mes === d.mes) || d.serie[d.serie.length - 1]
  const ent = cur.entradas + cur.entradas_prev, sai = cur.saidas + cur.saidas_prev
  const resultado = ent - sai
  const temPrev = cur.entradas_prev > 0 || cur.saidas_prev > 0

  const exportar = () => {
    const head = ['Mês', 'Saldo inicial', 'Entradas', 'A receber', 'Saídas', 'A pagar', 'Aplicações/transf. entrada', 'Aplicações/transf. saída', 'Saldo final']
    const f = (v: number) => v.toFixed(2).replace('.', ',')
    const lines = serie.map(m => [m.mes, f(m.saldo_inicial), f(m.entradas), f(m.entradas_prev), f(m.saidas), f(m.saidas_prev), f(m.transf_in), f(m.transf_out), f(m.saldo_final)].join(';'))
    const url = URL.createObjectURL(new Blob(['\uFEFF' + [head.join(';'), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' }))
    const a = document.createElement('a'); a.href = url; a.download = `fluxo-caixa-mes-a-mes-${d.hoje}.csv`; a.click(); URL.revokeObjectURL(url)
  }

  return (
    <>
      {error && <div className="shrink-0 text-sm text-red-500">{error}</div>}
      <PlnKpis>
        <PlnKpi rotulo={`Início de ${mesCurto(cur.mes)}`} valor={brl(cur.saldo_inicial)} pe="saldo das contas no 1º dia" />
        <PlnKpi rotulo="Entradas" cor={MONEY.in} tom="ok" valor={brl(ent)} pe={cur.entradas_prev ? `inclui ${brl(cur.entradas_prev)} a receber` : 'realizadas no mês'} />
        <PlnKpi rotulo="Saídas" cor={MONEY.out} tom="ruim" valor={brl(sai)} pe={cur.saidas_prev ? `inclui ${brl(cur.saidas_prev)} a pagar` : 'realizadas no mês'} />
        <PlnKpi rotulo="Resultado do mês" tom={resultado >= 0 ? 'ok' : 'ruim'} valor={brl(resultado)} pe="entradas menos saídas" />
        <PlnKpi rotulo={`Fim de ${mesCurto(cur.mes)}`} cor={MONEY.balance} tom={cur.saldo_final < 0 ? 'ruim' : undefined} valor={brl(cur.saldo_final)} pe={temPrev || cur.futuro ? 'previsto, com o que vence' : 'saldo no último dia'} />
      </PlnKpis>

      <div className="pln-visao flex-1 min-h-0">
        <PlnBloco titulo="Entradas × saídas" nota="cheio = realizado · claro = a vencer · linha = saldo no fim do mês"
          extra={<div className="pln-legenda"><span><b style={{ background: MONEY.in }} />Entradas</span><span><b style={{ background: MONEY.out }} />Saídas</span><span><b style={{ background: MONEY.balance }} />Saldo</span></div>}
          className="pln-visao-graf">
          <div className={`flex-1 min-h-0 px-3 pt-3 pb-2 transition-opacity ${loading ? 'opacity-60' : ''}`}>
            <MonthlyFlowChart serie={serie} selected={d.mes} onSelect={setMes} />
          </div>
        </PlnBloco>

        <PlnBloco titulo={monthLabel(d.mes)} nota="por categoria · clique num mês do gráfico ou da tabela" className="pln-visao-cat">
          <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin px-[18px] pb-4">
            <CategoriasDoPeriodo itens={d.detalhe} />
          </div>
        </PlnBloco>

        <PlnBloco titulo="Mês a mês" nota="clique para ver o mês"
          extra={<button onClick={exportar} className="neu-convex inline-flex items-center gap-2 rounded-lg border border-border text-sm font-medium px-3 py-1.5 hover:bg-secondary"><FiDownload className="w-4 h-4" /> Exportar</button>}
          className="pln-visao-tab">
          <div className="glass-panel neu-table flex-1 min-h-0 overflow-auto scrollbar-thin">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="text-left text-xs text-muted-foreground">
                  <th className={`px-4 py-2.5 font-medium ${stickyTh}`}>Mês</th>
                  <th className={`px-4 py-2.5 font-medium text-right ${stickyTh}`}>Entradas</th>
                  <th className={`px-4 py-2.5 font-medium text-right ${stickyTh}`}>Saídas</th>
                  <th className={`px-4 py-2.5 font-medium text-right ${stickyTh}`} title="Aplicação/resgate e transferências entre contas: mexem no saldo, mas não são operação">Aplic./transf.</th>
                  <th className={`px-4 py-2.5 font-medium text-right ${stickyTh}`}>Resultado</th>
                  <th className={`px-4 py-2.5 font-medium text-right ${stickyTh}`}>Saldo final</th>
                </tr>
              </thead>
              <tbody>
                {[...serie].reverse().map(m => {
                  const e = m.entradas + m.entradas_prev, sa = m.saidas + m.saidas_prev, tr = m.transf_in - m.transf_out
                  return (
                    <tr key={m.mes} onClick={() => setMes(m.mes)} className={`neu-tr neu-tr-click border-b border-border/60 ${m.mes === d.mes ? 'neu-tr-on pln-sel' : ''}`}>
                      <td className="px-4 py-2 whitespace-nowrap capitalize">
                        {mesCurto(m.mes)}
                        {m.mes === d.hoje && <span className="pln-pilula ml-2">atual</span>}
                        {m.futuro && <span className="text-muted-foreground ml-2 normal-case">previsto</span>}
                      </td>
                      <td className="px-4 py-2 text-right text-primary tabular-nums">{brl(e)}</td>
                      <td className="px-4 py-2 text-right text-red-600 dark:text-red-400 tabular-nums">{brl(sa)}</td>
                      <td className="px-4 py-2 text-right text-muted-foreground tabular-nums">{tr ? brl(tr) : '—'}</td>
                      <td className={`px-4 py-2 text-right tabular-nums ${e - sa >= 0 ? 'text-primary' : 'text-red-600 dark:text-red-400'}`}>{brl(e - sa)}</td>
                      <td className={`px-4 py-2 text-right font-semibold tabular-nums ${m.saldo_final < 0 ? 'text-red-600 dark:text-red-400' : 'text-foreground'}`}>{brl(m.saldo_final)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </PlnBloco>
      </div>
    </>
  )
}

// Categorias de um recorte, com barra realizado (cheio) + a vencer (claro).
// Compartilhado pela Visão geral (mês) e por Diário/Ano/Período (intervalo).
function CategoriasDoPeriodo({ itens }: { itens: CatMes[] }) {
  const max = Math.max(1, ...itens.map(c => c.real_v + c.prev_v))
  const Lista = ({ lista, cor }: { lista: CatMes[]; cor: string }) => (
    <>
      {lista.map(c => (
        <div key={`${c.kind}-${c.id}`} className="pln-cat" title={c.prev_v ? `${brl(c.real_v)} realizado + ${brl(c.prev_v)} a vencer` : undefined}>
          <span className="nome">{c.name}</span>
          <span className="valor">{brl(c.real_v + c.prev_v)}</span>
          <div className="barra">
            <i style={{ width: `${(c.real_v / max) * 100}%`, background: cor }} />
            <i style={{ width: `${(c.prev_v / max) * 100}%`, background: cor, opacity: 0.35 }} />
          </div>
        </div>
      ))}
      {!lista.length && <p className="pln-vazio py-2">Nada neste recorte.</p>}
    </>
  )
  return (
    <>
      <div className="pln-cat-sec ok">Entradas</div>
      <Lista lista={itens.filter(c => c.kind === 'receita')} cor={MONEY.in} />
      <div className="pln-cat-sec ruim">Saídas</div>
      <Lista lista={itens.filter(c => c.kind === 'despesa')} cor={MONEY.out} />
    </>
  )
}

// ── Aba 2: Análise por categorias ──
const SITUACOES: [string, string][] = [['', 'Todas'], ['em_aberto', 'Em aberto'], ['vencido', 'Vencido'], ['pago', 'Baixado']]
type CatSortKey = 'valor' | 'av' | 'em_aberto' | 'vencido' | 'baixado'

function CategoriasTab() {
  const { authenticatedFetch } = useAuth()
  const [month, setMonth] = useState(thisMonth())
  const [kind, setKind] = useState('')
  const [situacao, setSituacao] = useState('')
  const [data, setData] = useState<CatData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [sort, setSort] = useState<{ key: CatSortKey; dir: 1 | -1 }>({ key: 'valor', dir: -1 })
  const [selCat, setSelCat] = useState<string | null>(null)
  // Drill-down: qual categoria está aberta. `id: null` é "(sem categoria)", que
  // é uma linha legítima (sobra de rateio ainda não classificada).
  const [drill, setDrill] = useState<{ id: number | null; name: string } | null>(null)

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    setSelCat(null) // seleção não sobrevive a troca de mês/filtro (senão esmaece o donut inteiro)
    try {
      const params = new URLSearchParams({ period: month })
      if (kind) params.set('kind', kind)
      if (situacao) params.set('situacao', situacao)
      const r = await authenticatedFetch(`/api/fluxo-caixa/categorias?${params}`)
      const d = await r.json()
      if (d.success) setData(d); else setError(d.error || 'Falha ao carregar')
    } catch { setError('Erro de conexão') }
    finally { setLoading(false) }
  }, [authenticatedFetch, month, kind, situacao])
  useEffect(() => { load() }, [load])

  const cats = data?.categorias || []

  // Ordenação client-side (a lista é pequena — ~110 categorias no pior caso)
  const sorted = useMemo(() => [...cats].sort((a, b) => (a[sort.key] - b[sort.key]) * sort.dir), [cats, sort])
  const receitas = useMemo(() => cats.filter(c => c.category_kind === 'receita').reduce((s, c) => s + c.valor, 0), [cats])
  const despesas = useMemo(() => cats.filter(c => c.category_kind !== 'receita').reduce((s, c) => s + c.valor, 0), [cats])
  const totSit = useMemo(() => cats.reduce((t, c) => ({ em_aberto: t.em_aberto + c.em_aberto, vencido: t.vencido + c.vencido, baixado: t.baixado + c.baixado }), { em_aberto: 0, vencido: 0, baixado: 0 }), [cats])

  // Donut top-7 + "Outras" (sempre por valor, independente da ordenação da tabela)
  const donut = useMemo(() => {
    if (!cats.length) return []
    const byValor = [...cats].sort((a, b) => b.valor - a.valor)
    const top = byValor.slice(0, 7).map(c => ({ name: c.category_name, value: c.valor }))
    const restV = byValor.slice(7).reduce((s, c) => s + c.valor, 0)
    return restV > 0 ? [...top, { name: 'Outras', value: restV }] : top
  }, [cats])

  const selectCat = (name: string) => {
    if (name === 'Outras') return // fatia agregada, não tem linha correspondente na tabela
    setSelCat(s => {
      const next = s === name ? null : name
      if (next) requestAnimationFrame(() => {
        document.querySelector(`[data-cat="${CSS.escape(name)}"]`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      })
      return next
    })
  }

  const toggleSort = (key: CatSortKey) => setSort(s => ({ key, dir: s.key === key ? (s.dir === -1 ? 1 : -1) : -1 }))
  const SortTh = ({ k, w, children }: { k: CatSortKey; w: string; children: ReactNode }) => (
    <th className={`px-4 py-2.5 font-medium text-right ${w} ${stickyTh}`}>
      <button onClick={() => toggleSort(k)} title="Ordenar por esta coluna" className="inline-flex items-center gap-1 hover:text-foreground transition-colors">
        {children}
        {sort.key === k && (sort.dir === -1 ? <FiChevronDown className="w-3 h-3" /> : <FiChevronUp className="w-3 h-3" />)}
      </button>
    </th>
  )

  return (
    <div className="flex-1 min-h-0 flex flex-col pln-corpo-tela">
      <PlnBarra>
        <PlnBarraRot>Mês</PlnBarraRot>
        <div className="flex items-center gap-2">
          <button onClick={() => setMonth(m => addMonth(m, -1))} className="neu-convex w-8 h-8 flex items-center justify-center rounded-md border border-border hover:bg-secondary"><FiChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm font-medium text-foreground min-w-[140px] text-center">{monthLabel(month)}</span>
          <button onClick={() => setMonth(m => addMonth(m, 1))} className="neu-convex w-8 h-8 flex items-center justify-center rounded-md border border-border hover:bg-secondary"><FiChevronRight className="w-4 h-4" /></button>
        </div>
        <PlnBarraSep />
        <PlnBarraRot>Tipo</PlnBarraRot>
        <div className="neu-seg inline-flex rounded-lg border border-border p-0.5 bg-card">
          {([['', 'Todos'], ['receita', 'A receber'], ['despesa', 'A pagar']] as [string, string][]).map(([v, lbl]) => (
            <button key={v || 'all'} onClick={() => setKind(v)} className={`px-3 py-1.5 text-sm rounded-md transition-colors ${kind === v ? 'neu-relief bg-primary/10 text-primary font-medium' : 'neu-seg-btn text-muted-foreground hover:text-foreground'}`}>{lbl}</button>
          ))}
        </div>
        <PlnBarraSep />
        <PlnBarraRot>Situação</PlnBarraRot>
        <div className="neu-seg inline-flex rounded-lg border border-border p-0.5 bg-card">
          {SITUACOES.map(([v, lbl]) => (
            <button key={v || 'all'} onClick={() => setSituacao(v)} className={`px-3 py-1.5 text-sm rounded-md transition-colors ${situacao === v ? 'neu-relief bg-primary/10 text-primary font-medium' : 'neu-seg-btn text-muted-foreground hover:text-foreground'}`}>{lbl}</button>
          ))}
        </div>
      </PlnBarra>

      {error && <div className="shrink-0 text-sm text-red-500">{error}</div>}

      {/* Números do mês por vencimento (21/10/2026): o resumo Receitas/Despesas
          que morava num cartãozinho embaixo do donut virou a fileira de cartões
          do desenho aprovado, com a situação (aberto/vencido/baixado). */}
      {!loading && cats.length > 0 && (
        <PlnKpis>
          <PlnKpi rotulo="Receitas" cor={MONEY.in} tom="ok" valor={brl(receitas)} pe="por vencimento no mês" />
          <PlnKpi rotulo="Despesas" cor={MONEY.out} tom="ruim" valor={brl(despesas)} pe="por vencimento no mês" />
          <PlnKpi rotulo="Em aberto" valor={brl(totSit.em_aberto)} pe="ainda a vencer" />
          <PlnKpi rotulo="Vencido" tom={totSit.vencido ? 'ruim' : undefined} valor={brl(totSit.vencido)} pe="passou do vencimento sem baixa" />
          <PlnKpi rotulo="Baixado" valor={brl(totSit.baixado)} pe="pago ou recebido (líquido)" />
        </PlnKpis>
      )}

      {/* Corpo em 2 colunas (xl+): donut/resumo fixos à esquerda, tabela
          full-height à direita com scroll interno. Abaixo de xl empilha, com o
          bloco do donut limitado a 45% da altura (rola sozinho se estourar). */}
      <div className="flex-1 min-h-0 flex flex-col xl:flex-row gap-4">
        {!loading && donut.length > 0 && (
          <div className="shrink-0 min-h-0 overflow-y-auto scrollbar-thin max-h-[45%] xl:max-h-none xl:w-[400px] flex flex-col gap-4">
            <PlnBloco titulo="Distribuição por categoria" nota="clique numa fatia para destacar">
              <div className="px-2 pb-3"><CategoryDonut data={donut} total={data!.total} selected={selCat} onSelect={selectCat} /></div>
            </PlnBloco>
          </div>
        )}

        <PlnBloco titulo="Categorias" nota={loading ? undefined : `${sorted.length} categorias · clique numa linha para ver os itens`} className="flex-1 min-h-0 min-w-0">
        <div className="glass-panel neu-table flex-1 min-h-0 overflow-auto scrollbar-thin">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="text-left text-xs text-muted-foreground">
                <th className={`px-4 py-2.5 font-medium ${stickyTh}`}>Categoria</th>
                <SortTh k="valor" w="w-32">Valor</SortTh>
                <SortTh k="av" w="w-24">AV %</SortTh>
                <SortTh k="em_aberto" w="w-28">Em aberto</SortTh>
                <SortTh k="vencido" w="w-28">Vencido</SortTh>
                <SortTh k="baixado" w="w-32">Baixado (líq.)</SortTh>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground"><FiLoader className="w-5 h-5 animate-spin inline" /></td></tr>
              ) : sorted.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">Nenhum lançamento no período.</td></tr>
              ) : sorted.map(c => (
                <tr key={c.category_id} data-cat={c.category_name}
                  className={`border-b border-border/60 transition-colors ${selCat === c.category_name ? 'bg-primary/10' : 'hover:bg-secondary/40'}`}>
                  <td className="px-4 py-2.5">
                    {/* Clicar entra no número: mostra os lançamentos que o
                        formaram, com o rateio já expandido. */}
                    <button onClick={() => setDrill({ id: c.category_id ?? null, name: c.category_name })}
                      title="Ver os lançamentos desta categoria no período"
                      className="text-foreground hover:text-primary hover:underline text-left">
                      {c.category_name}
                    </button>
                    <span className={`ml-2 text-[10px] rounded-full px-1.5 py-0.5 neu-tag ${c.category_kind === 'receita' ? 'bg-primary/10 text-primary' : 'bg-red-500/10 text-red-600 dark:text-red-400'}`}>{c.category_kind === 'receita' ? 'receita' : 'despesa'}</span>
                  </td>
                  <td className="px-4 py-2.5 text-right font-medium text-foreground tabular-nums">{brl(c.valor)}</td>
                  <td className="px-4 py-2.5 text-right text-muted-foreground">
                    <div className="inline-flex flex-col items-end gap-1">
                      <span className="tabular-nums">{c.av.toFixed(1)}%</span>
                      <span className="w-14 h-1 rounded-full bg-secondary neu-gauge overflow-hidden">
                        <span className="block h-full rounded-full bg-primary/60" style={{ width: `${Math.min(100, Math.max(0, c.av))}%` }} />
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-right text-amber-600 dark:text-amber-400 tabular-nums">{c.em_aberto ? brl(c.em_aberto) : '—'}</td>
                  <td className="px-4 py-2.5 text-right text-red-600 dark:text-red-400 tabular-nums">{c.vencido ? brl(c.vencido) : '—'}</td>
                  <td className="px-4 py-2.5 text-right text-primary tabular-nums">{c.baixado ? brl(c.baixado) : '—'}</td>
                </tr>
              ))}
            </tbody>
            {data && sorted.length > 0 && (
              <tfoot>
                <tr className="font-medium">
                  <td className={`px-4 py-2.5 text-muted-foreground ${stickyFoot}`}>Total geral</td>
                  <td className={`px-4 py-2.5 text-right text-foreground tabular-nums ${stickyFoot}`}>{brl(data.total)}</td>
                  <td className={`px-4 py-2.5 text-right text-muted-foreground ${stickyFoot}`}>100%</td>
                  <td colSpan={3} className={stickyFoot}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        </PlnBloco>
      </div>

      {drill && (
        <ItensCategoria
          categoryId={drill.id}
          categoryName={drill.name}
          month={month}
          kind={kind}
          situacao={situacao}
          onClose={() => setDrill(null)}
        />
      )}
    </div>
  )
}
